import React, { useEffect, useRef, useState } from "react";
import "./ClaimApp.css";
import { useDispatch, useSelector } from "react-redux";
import { ApiPaths } from "../../Config/ApiPath";
import useAxiosHelper from "../../Common/AxiosHelper";
import { useSearchParams } from "react-router-dom";
import { setIncomeWallet } from "../../Redux/IncomeWallet";
import { BasicInfo, toastFailed, toastSuccess } from "../../Config/BasicInfo";
import ClaimedHistory from "../WithdrawalHistory/WithdrawalHistory";
import EditAccount from "../EditAccount/EditAccount";
import coinLogo from "../../Images/coin.png";
import {
  Wallet,
  ArrowUpRight,
  History,
  ShieldCheck,
  AlertTriangle,
  ChevronRight,
  Check,
  Landmark,
  X,
  Info,
  Plus,
} from "lucide-react";

const VALID_TABS = ["withdraw", "payment"];

/* Current withdrawal rules for main_wallet (advance_info.withdrawal.main_wallet
   on the server). The user API doesn't expose them, so they're shown here for
   guidance only — the backend validates every request. */
const MIN_WITHDRAWAL = 10;
const SERVICE_FEE_PCT = 5;

const WITHDRAW_WALLET_SLUGS = ["main_wallet", "token_wallet"];

const shortAddr = (a) => (a && a.length > 16 ? `${a.slice(0, 8)}…${a.slice(-6)}` : a || "");

const Claim = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = VALID_TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "withdraw";
  const { AxiosPost, AxiosGet } = useAxiosHelper();
  const incomeData = useSelector((state) => state.incomeData.incomeWallet);

  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [accountDetails, setAccountDetails] = useState();
  const [selectedAccount, setSelectedAccount] = useState("");
  const [withdrawWallet, setWithdrawWallet] = useState("main_wallet");
  const [refresh, setRefresh] = useState(false);
  const [withdrawHistory, setWithdrawHistory] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [accountPicker, setAccountPicker] = useState(false);
  const [done, setDone] = useState(null); // success summary
  const otpRefs = useRef([]);

  const withdrawableWallets = Array.isArray(incomeData)
    ? WITHDRAW_WALLET_SLUGS.map((slug) => incomeData.find((w) => w?.slug === slug)).filter(Boolean)
    : [];
  const activeWithdrawWallet = withdrawableWallets.find((w) => w?.slug === withdrawWallet) || withdrawableWallets[0];
  const isTokenWithdraw = activeWithdrawWallet?.slug === "token_wallet";
  const availableBalance = parseFloat(activeWithdrawWallet?.value) || 0;
  const otherWallets = Array.isArray(incomeData)
    ? incomeData.filter(
        (w) => w?.wallet_type === "wallet" && !WITHDRAW_WALLET_SLUGS.includes(w.slug) && w.slug !== "fund_wallet"
      )
    : [];
  const fundWallet = Array.isArray(incomeData) ? incomeData.find((w) => w?.slug === "fund_wallet") : null;

  const hasAccounts = Array.isArray(accountDetails) && accountDetails.length > 0;

  const fmtUsd = (v) =>
    `$${(parseFloat(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtTok = (v) =>
    (parseFloat(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const fmtAmt = (v) => (isTokenWithdraw ? fmtTok(v) : fmtUsd(v));

  const num = Number(amount);
  const fee = isTokenWithdraw || !(num > 0) ? 0 : (num * SERVICE_FEE_PCT) / 100;
  const receive = Math.max(0, (num > 0 ? num : 0) - fee);

  useEffect(() => {
    fetchWallets();
    getWalletAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh accounts & balances when returning to the Withdraw tab
  // (e.g. after adding a payout account under "Payout Account").
  useEffect(() => {
    if (activeTab === "withdraw") {
      getWalletAddress();
      fetchWallets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // a single payout account is selected automatically
  useEffect(() => {
    if (hasAccounts && accountDetails.length === 1 && !selectedAccount) {
      setSelectedAccount(accountDetails[0]?.address);
    }
  }, [accountDetails, hasAccounts, selectedAccount]);

  // sheets: Escape closes, page behind stays put
  const sheetOpen = showOtp || accountPicker || !!done;
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !otpLoading) {
        setShowOtp(false);
        setAccountPicker(false);
        setDone(null);
      }
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [sheetOpen, otpLoading]);

  useEffect(() => {
    if (showOtp) setTimeout(() => otpRefs.current[0]?.focus(), 120);
  }, [showOtp]);

  async function fetchWallets() {
    try {
      const response = await AxiosGet(ApiPaths.getWallets);
      if (response?.wallets) dispatch(setIncomeWallet(response.wallets));
    } catch (e) {
      BasicInfo.isDebug && console.log(e);
    }
  }

  const getWalletAddress = async () => {
    try {
      const response = await AxiosGet(ApiPaths.getWalletAddress);
      setAccountDetails(response?.web3Details || []);
    } catch (error) {
      console.error(error.message);
    }
  };

  const validate = () => {
    setAmountError("");
    if (!hasAccounts) {
      toastFailed("Please add a withdrawal account first");
      setSearchParams({ tab: "payment" });
      return false;
    }
    if (!selectedAccount || selectedAccount === "0") {
      toastFailed("Please select an account");
      setAccountPicker(true);
      return false;
    }
    if (!amount || isNaN(num) || num <= 0) {
      setAmountError("Enter an amount to withdraw");
      return false;
    }
    if (!isTokenWithdraw && num < MIN_WITHDRAWAL) {
      setAmountError(`Minimum withdrawal is ${fmtUsd(MIN_WITHDRAWAL)}`);
      return false;
    }
    if (num > availableBalance) {
      setAmountError(`Amount exceeds your available balance (${fmtAmt(availableBalance)})`);
      return false;
    }
    return true;
  };

  const handleSendOtp = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      await AxiosPost(ApiPaths.sendOTP, { action: "withdrawal" });
      toastSuccess("OTP sent to your registered email");
      setOtp(Array(6).fill(""));
      setOtpError("");
      setShowOtp(true);
    } catch (error) {
      toastFailed(error?.response?.data?.message || error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    setOtpError("");
    const code = otp.join("");
    if (code.length !== 6) {
      setOtpError("Enter the 6-digit code from your email");
      return;
    }
    try {
      setOtpLoading(true);
      const body = {
        amount: Number(amount),
        wallet_name: withdrawWallet,
        account: selectedAccount,
        paymentMethod: "web3",
        otp: code,
        action: "withdrawal",
      };
      const tempWithdrawData = await AxiosPost(ApiPaths.withdraw, body);
      toastSuccess(tempWithdrawData?.message);
      setShowOtp(false);
      setDone({ amount: num, receive, account: selectedAccount, message: tempWithdrawData?.message });
      setOtp(Array(6).fill(""));
      setAmount("");
      fetchWallets();
      setRefresh((prev) => !prev);
    } catch (error) {
      setOtpError(error?.response?.data?.message || error?.message || "Withdrawal failed");
    } finally {
      setOtpLoading(false);
    }
  };

  /* OTP boxes */
  const setDigit = (i, v) => {
    setOtpError("");
    const clean = v.replace(/\D/g, "");
    setOtp((d) => {
      const next = [...d];
      if (!clean) {
        next[i] = "";
        return next;
      }
      clean
        .slice(0, 6 - i)
        .split("")
        .forEach((ch, k) => {
          next[i + k] = ch;
        });
      return next;
    });
    if (clean) otpRefs.current[Math.min(i + clean.length, 5)]?.focus();
  };
  const onDigitKey = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
    if (e.key === "Enter") handleWithdraw();
  };

  const setPct = (p) => {
    const v = p === 1 ? availableBalance : Math.floor(availableBalance * p * 100) / 100;
    setAmountError("");
    setAmount(v > 0 ? String(v) : "");
  };

  return (
    <section className="wdPage">
      {/* ---------- Balance hero ---------- */}
      <div className="wdHero">
        <div className="wdHeroTop">
          <span className="wdEyebrow">
            <ArrowUpRight size={13} /> Withdraw
          </span>
          {activeTab === "withdraw" && !withdrawHistory && (
            <button type="button" className="wdHeroBtn" onClick={() => setWithdrawHistory(true)}>
              <History size={14} /> History
            </button>
          )}
        </div>
        <div className="wdHeroLabel">
          Available in {activeWithdrawWallet?.name || "Main Wallet"}
        </div>
        <div className="wdHeroValue">
          {isTokenWithdraw ? (
            <>
              <img src={coinLogo} alt="" className="wdCoin" /> {fmtTok(availableBalance)}
            </>
          ) : (
            fmtUsd(availableBalance)
          )}
        </div>
        <div className="wdHeroChips">
          {fundWallet && (
            <span>
              <Wallet size={12} /> Fund {fmtUsd(fundWallet.value)}
            </span>
          )}
          {otherWallets.map((w) => (
            <span key={w.slug}>
              <Wallet size={12} /> {w.name} {w.slug === "swap_wallet" ? `MTX ${fmtTok(w.value)}` : fmtUsd(w.value)}
            </span>
          ))}
          <span>
            <Info size={12} /> Min {fmtUsd(MIN_WITHDRAWAL)} · {SERVICE_FEE_PCT}% fee
          </span>
        </div>
      </div>

      {/* ---------- Tabs ---------- */}
      <div className="wdTabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "withdraw"}
          className={`wdTab ${activeTab === "withdraw" ? "isOn" : ""}`}
          onClick={() => setSearchParams({ tab: "withdraw" })}
        >
          <ArrowUpRight size={16} /> Withdraw
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "payment"}
          className={`wdTab ${activeTab === "payment" ? "isOn" : ""}`}
          onClick={() => setSearchParams({ tab: "payment" })}
        >
          <Landmark size={16} /> Payout Account
        </button>
      </div>

      {activeTab === "withdraw" &&
        (withdrawHistory ? (
          <ClaimedHistory refresh={refresh} onBack={() => setWithdrawHistory(false)} />
        ) : (
          <div className="wdForm">
            {withdrawableWallets.length > 1 && (
              <div className="wdSeg" role="group" aria-label="Withdraw from">
                {withdrawableWallets.map((w) => (
                  <button
                    key={w.slug}
                    type="button"
                    className={withdrawWallet === w.slug ? "isOn" : ""}
                    onClick={() => {
                      setWithdrawWallet(w.slug);
                      setAmount("");
                      setAmountError("");
                    }}
                  >
                    {w.name}
                  </button>
                ))}
              </div>
            )}

            {/* To account */}
            <div className="wdCard">
              <div className="wdCardLabel">Send to</div>
              {hasAccounts ? (
                <button
                  type="button"
                  className="wdAccount"
                  onClick={() => accountDetails.length > 1 && setAccountPicker(true)}
                >
                  <span className="wdAccIcon">
                    <Landmark size={18} />
                  </span>
                  <span className="wdAccText">
                    <b>{selectedAccount ? shortAddr(selectedAccount) : "Choose an account"}</b>
                    <small>
                      {(accountDetails.find((a) => a?.address === selectedAccount)?.chain || "BEP20").toUpperCase()} · USDT
                    </small>
                  </span>
                  {accountDetails.length > 1 && <ChevronRight size={18} className="wdChev" />}
                </button>
              ) : (
                <button type="button" className="wdNoAccount" onClick={() => setSearchParams({ tab: "payment" })}>
                  <AlertTriangle size={18} />
                  <span>
                    <b>No payout account yet</b>
                    <small>Add a BEP20 USDT address to withdraw</small>
                  </span>
                  <span className="wdAddPill">
                    <Plus size={14} /> Add
                  </span>
                </button>
              )}
            </div>

            {/* Amount */}
            <div className={`wdCard ${amountError ? "isError" : ""}`}>
              <div className="wdCardLabel">
                Amount
                <span>
                  Available {fmtAmt(availableBalance)}
                </span>
              </div>
              <label className="wdAmount">
                {!isTokenWithdraw && <span>$</span>}
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => {
                    setAmountError("");
                    setAmount(e.target.value);
                  }}
                  aria-label="Withdrawal amount"
                />
              </label>
              <div className="wdPcts">
                {[
                  { l: "25%", p: 0.25 },
                  { l: "50%", p: 0.5 },
                  { l: "75%", p: 0.75 },
                  { l: "Max", p: 1 },
                ].map((c) => (
                  <button key={c.l} type="button" onClick={() => setPct(c.p)} disabled={availableBalance <= 0}>
                    {c.l}
                  </button>
                ))}
              </div>
              {amountError && (
                <p className="wdError">
                  <AlertTriangle size={14} /> {amountError}
                </p>
              )}
            </div>

            {/* Breakdown */}
            {!isTokenWithdraw && (
              <dl className="wdBreak">
                <div>
                  <dt>Withdrawal amount</dt>
                  <dd>{fmtUsd(num > 0 ? num : 0)}</dd>
                </div>
                <div>
                  <dt>Service fee ({SERVICE_FEE_PCT}%)</dt>
                  <dd className="neg">−{fmtUsd(fee)}</dd>
                </div>
                <div className="total">
                  <dt>You receive</dt>
                  <dd>{fmtUsd(receive)}</dd>
                </div>
              </dl>
            )}

            <div className="wdCtaBar">
              <button type="button" className="wdCta" onClick={handleSendOtp} disabled={loading || availableBalance <= 0}>
                {loading ? <span className="wdSpin" /> : <ShieldCheck size={18} />}
                {loading ? "Sending code…" : "Continue"}
              </button>
              <p className="wdSecure">
                <ShieldCheck size={12} /> Secured with an email verification code
              </p>
            </div>
          </div>
        ))}

      {activeTab === "payment" && <EditAccount embedded />}

      {/* ---------- Account picker ---------- */}
      {accountPicker && hasAccounts && (
        <div className="wdOverlay" onClick={() => setAccountPicker(false)}>
          <div className="wdSheet" role="dialog" aria-modal="true" aria-labelledby="wdPickTitle" onClick={(e) => e.stopPropagation()}>
            <span className="wdHandle" aria-hidden="true" />
            <button type="button" className="wdClose" onClick={() => setAccountPicker(false)} aria-label="Close">
              <X size={18} />
            </button>
            <h3 id="wdPickTitle">Send to</h3>
            <div className="wdPickList">
              {accountDetails.map((a, i) => (
                <button
                  key={a?.address || i}
                  type="button"
                  className={`wdAccount ${selectedAccount === a?.address ? "isOn" : ""}`}
                  onClick={() => {
                    setSelectedAccount(a?.address);
                    setAccountPicker(false);
                  }}
                >
                  <span className="wdAccIcon">
                    <Landmark size={18} />
                  </span>
                  <span className="wdAccText">
                    <b>{shortAddr(a?.address)}</b>
                    <small>{(a?.chain || "BEP20").toUpperCase()} · USDT</small>
                  </span>
                  {selectedAccount === a?.address && <Check size={18} className="wdTick" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ---------- OTP / confirm ---------- */}
      {showOtp && (
        <div className="wdOverlay" onClick={() => !otpLoading && setShowOtp(false)}>
          <div className="wdSheet" role="dialog" aria-modal="true" aria-labelledby="wdOtpTitle" onClick={(e) => e.stopPropagation()}>
            <span className="wdHandle" aria-hidden="true" />
            <button type="button" className="wdClose" onClick={() => setShowOtp(false)} disabled={otpLoading} aria-label="Close">
              <X size={18} />
            </button>
            <h3 id="wdOtpTitle">Confirm withdrawal</h3>
            <p className="wdSheetSub">Enter the 6-digit code sent to your registered email.</p>

            <div className="wdSummary">
              <div>
                <small>You receive</small>
                <b>{isTokenWithdraw ? fmtTok(num) : fmtUsd(receive)}</b>
              </div>
              <span>
                to {shortAddr(selectedAccount)}
              </span>
            </div>

            <div className="wdOtp" role="group" aria-label="Verification code">
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  className={`${d ? "isFilled" : ""} ${otpError ? "isError" : ""}`}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  maxLength={6}
                  value={d}
                  aria-label={`Digit ${i + 1}`}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => onDigitKey(i, e)}
                  onFocus={(e) => e.target.select()}
                />
              ))}
            </div>
            {otpError && (
              <p className="wdError center">
                <AlertTriangle size={14} /> {otpError}
              </p>
            )}

            <div className="wdSheetActions">
              <button
                type="button"
                className="wdBtn ghost"
                onClick={() => {
                  setOtp(Array(6).fill(""));
                  setShowOtp(false);
                  setOtpError("");
                }}
                disabled={otpLoading}
              >
                Cancel
              </button>
              <button type="button" className="wdBtn primary" onClick={handleWithdraw} disabled={otpLoading}>
                {otpLoading ? (
                  <>
                    <span className="wdSpin" /> Processing…
                  </>
                ) : (
                  "Confirm withdrawal"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Success ---------- */}
      {done && (
        <div className="wdOverlay" onClick={() => setDone(null)}>
          <div className="wdSheet center" role="dialog" aria-modal="true" aria-labelledby="wdDoneTitle" onClick={(e) => e.stopPropagation()}>
            <span className="wdHandle" aria-hidden="true" />
            <span className="wdDoneIcon">
              <Check size={34} />
            </span>
            <h3 id="wdDoneTitle">Withdrawal requested</h3>
            <p className="wdSheetSub">{done.message || "Your request is being processed."}</p>
            <dl className="wdBreak">
              <div>
                <dt>Requested</dt>
                <dd>{fmtAmt(done.amount)}</dd>
              </div>
              {!isTokenWithdraw && (
                <div className="total">
                  <dt>You receive</dt>
                  <dd>{fmtUsd(done.receive)}</dd>
                </div>
              )}
              <div>
                <dt>To</dt>
                <dd>{shortAddr(done.account)}</dd>
              </div>
            </dl>
            <div className="wdSheetActions">
              <button
                type="button"
                className="wdBtn ghost"
                onClick={() => {
                  setDone(null);
                  setWithdrawHistory(true);
                }}
              >
                <History size={16} /> View history
              </button>
              <button type="button" className="wdBtn primary" onClick={() => setDone(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Claim;
